import { useState, useRef, useEffect } from 'react';
import { Tooltip } from '@mantine/core';

function fmt(v) {
  if (v == null || isNaN(v)) return '0.00';
  return Number(v).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function VatField({ field, onChange }) {
  const [editing, setEditing] = useState(false);
  const [draftValue, setDraftValue] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  if (!field) {
    return <span className="vat-field__value">{fmt(null)}</span>;
  }

  const { value, computed, override, editable, code } = field;

  if (!editable) {
    return <span className="vat-field__value" aria-label={`Field ${code}: ${fmt(value)}`}>{fmt(value)}</span>;
  }

  const handleClick = () => {
    if (!editing) {
      setDraftValue(String(value ?? 0));
      setEditing(true);
    }
  };

  const commit = () => {
    const parsed = parseFloat(draftValue);
    if (!isNaN(parsed) && parsed !== Number(value)) {
      onChange(code, parsed);
    }
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      commit();
    } else if (e.key === 'Escape') {
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        className="vat-field__input"
        type="number"
        step="0.01"
        value={draftValue}
        onChange={e => setDraftValue(e.target.value)}
        onBlur={commit}
        onKeyDown={handleInputKeyDown}
        aria-label={`Manual override for field ${code}`}
      />
    );
  }

  return (
    <Tooltip
      label={
        <div style={{ fontSize: 11 }}>
          <div>Computed: {fmt(computed)}</div>
          <div>Manual: {fmt(value)}</div>
          <div style={{ color: '#aaa', marginTop: 2 }}>Click to edit</div>
        </div>
      }
      withArrow
      openDelay={400}
    >
      <span
        className={`vat-field__value ${override ? 'vat-field__value--overridden' : ''}`}
        role="button"
        tabIndex={0}
        aria-label={`Field ${code}, manual value ${fmt(value)}${override ? ' (overridden)' : ''}. Activate to edit.`}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        style={{ cursor: 'pointer' }}
      >
        {fmt(value)}
        <span className="vat-field__icon" aria-hidden="true"> ✎</span>
      </span>
    </Tooltip>
  );
}
